import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Search } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { PageHeader } from "@/components/landing/PageHeader";
import { OrderStatusCard } from "@/components/landing/OrderStatusCard";
import { OrderHistory } from "@/components/landing/OrderHistory";
import { ForgotOrderNumber } from "@/components/landing/ForgotOrderNumber";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full h-13 py-4 px-5 rounded-2xl bg-[#faf9f6] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [ref, setRef] = useState(params.get("ref") || "");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  // Swap the card to another of this customer's orders without a new lookup
  const select = (o) => { setHistory((h) => [order, ...h.filter((x) => x.order_ref !== o.order_ref)].filter(Boolean)); setOrder(o); setRef(o.order_ref); const l = window.__lenis; l ? l.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); };

  const lookup = async (e, em = email, rf = ref) => {
    e?.preventDefault();
    setState("loading");
    setError("");
    setOrder(null);
    try {
      const r = await axios.post(`${API}/orders/lookup`, { email: em.trim(), order_ref: rf.trim() });
      const { other_orders = [], ...found } = r.data;
      setOrder(found);
      setHistory(other_orders);
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Could not look up this order. Try again.");
      setState("idle");
    }
  };

  useEffect(() => {
    const em = params.get("email"), rf = params.get("ref");
    if (em && rf) lookup(null, em, rf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="track-order-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="track" link={{ id: "contact", to: "/contact", label: "Contact" }} />
      <section className="relative pt-36 pb-24 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 hero-radial" />
        <div className="relative max-w-[1100px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-6 flex items-center gap-3"><span className="diamond" /> Order status</p>
            <h1 className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-6xl tracking-tight">Track <span className="text-pink">your piece</span></h1>
            <p className="mt-7 text-[#5f5e5a] text-base sm:text-lg leading-relaxed max-w-md">Enter the email you used at checkout and the 8-character order number from your confirmation email to see where your pre-order is.</p>
            <p className="mt-4 text-[#5f5e5a] text-sm leading-relaxed max-w-md">Can't find your order number? It's printed at the bottom of your confirmation email (e.g. <span className="font-mono-d text-[#0a0a0b]">#A1B2C3D4</span>).</p>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-7 space-y-6">
            <form data-testid="track-form" onSubmit={lookup} className="bg-[#faf9f6]/60 border border-[#e3e2dc] rounded-3xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4">
              <input data-testid="track-email" className={input} required type="email" placeholder="Email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input data-testid="track-ref" className={`${input} font-mono-d uppercase tracking-[0.15em]`} required minLength={6} maxLength={12} placeholder="Order no. e.g. A1B2C3D4" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} />
              {error && <p data-testid="track-error" className="sm:col-span-2 text-xs text-[#ff2a85] font-mono-d uppercase tracking-wider">{error}</p>}
              <button data-testid="track-submit" type="submit" disabled={state === "loading"} className="sm:col-span-2 h-14 rounded-full bg-[#0a0a0b] hover:bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
                {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <><Search size={14} /> Find my order</>}
              </button>
            </form>
            {order && <OrderStatusCard order={order} email={email} />}
            {order && <OrderHistory orders={history} onSelect={select} />}
            <ForgotOrderNumber initialEmail={email} />
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
